/**
 * PositionTableView — Dense multi-select position table.
 *
 * Powers the Positions tab of the Schedule page. One leg per row with:
 * - Multi-line rows: symbol/meta on line 1-2
 * - WAT/ORB/error as always-visible inline sub-rows (no expand/collapse)
 * - Checkboxes for multi-select + Shift+click range
 * - Colored column-group headers (ENTRY / SL / EXIT)
 * - Basket → Strategy grouping with collapsible sections at both levels
 * - Basket / Strategy Exit All / Cancel All action buttons
 * - Floating batch action bar (Exit / Cancel selected)
 * - Status-tinted rows
 * - Sticky checkbox+symbol (left) + actions (right) columns
 * - Keyboard shortcuts (Ctrl+A, Escape)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  LogOut,
  RotateCcw,
  TrendingUp,
  Unplug,
  X,
} from 'lucide-react';
import Badge from '../ui/Badge';
import ToggleSwitch from '../ui/ToggleSwitch';
import { shortRiskType as shortType } from '../../utils/triggerDescribe';
import { cn } from '../../utils/cn';
import { istParts } from '../../utils/dateUtils';
import { TH, TH_R, TH_C, SideBadge, slConfigDisplay, rowBg, rowOpacity } from './positionTableHelpers';
import PnlFlash from '../common/PnlFlash';
import CappedChip from './CappedChip';
import {
  formatPrice,
  formatQtyWithLots,
  getDisplayStatus,
  isCancellable,
  missedExitLabel,
  ReSequenceBadge,
  statusBadgeVariant,
} from '../../utils/positionDisplay';
import { buildNestedGroups, type BasketGroup, type StrategyGroup } from '../../utils/positionGrouping';
import { useRowSelection, type HeaderCheckboxState } from '../../hooks/useRowSelection';
import type { PositionRow } from '../../types';
import { StatusBoxRow } from '../common/StatusBoxRow';
import { StrategyPositionsModal } from './StrategyPositionsModal';
import { EntrySourceBadge } from '../ui/EntrySourceBadge';
import { CHIP_DENSE } from '../../utils/chipColors';
import EmptyStateWithRedirect from '../common/EmptyStateWithRedirect';
import { pickPositionsTableEmpty } from '../../utils/emptyStates';

// ── Props ──

interface PositionTableViewProps {
  positions: PositionRow[];
  onExit?: (p: PositionRow) => void;
  onExitMultiple?: (ids: string[]) => void;
  onRetry?: (p: PositionRow) => void;
  /** Platform-only "stop watching" (never calls the broker). Available on any
   *  non-terminal leg (OPEN / ENTRY_PENDING / EXIT_PENDING). */
  onTerminate?: (p: PositionRow) => void;
  exitingId?: string | null;
  retryingId?: string | null;
  terminatingId?: string | null;
  /** Drives the empty-state CTA: today → "Manage baskets"; historical
   *  → informational only (you weren't trading on this date). Defaults
   *  to true so legacy call sites get the today-flavored empty state. */
  isToday?: boolean;
}

// ── Tiny helpers ──

// TH, TH_R, TH_C, SideBadge, slConfigDisplay, rowBg, rowOpacity are imported
// from positionTableHelpers.tsx (see imports above) and re-exported below so
// any external code that still imports them from PositionTableView continues
// to work without changes.
export { TH, TH_R, TH_C, SideBadge, slConfigDisplay, rowBg, rowOpacity };

// Only rejection errors trigger the full-width detail sub-row. Missed-exit
// renders as a compact red-blink pill INSIDE the Status column (below the
// existing status Badge) so the indicator is colocated with the rightmost
// at-a-glance column and is impossible to miss — the long Symbol column
// is where the trader reads the contract, not status. `missedExitLabel`
// lives in utils/positionDisplay so the modal can reuse it.
const hasInlineDetail = (p: PositionRow) =>
  !!(p.entryStatus === 'REJECTED' && p.entryError);

/**
 * Compact "MISSED" pill rendered inside the Status column when a position has
 * `missedExit` set. Stacks below the displayStatus Badge in the same cell.
 * The full message (`missedExitLabel`) goes into the `title` so the user can
 * read it on hover; the pill itself stays narrow enough not to widen the
 * Status column. Blinks red to grab attention even when the row is mid-scroll.
 */
const MissedExitPill: React.FC<{ p: PositionRow }> = ({ p }) =>
  p.missedExit ? (
    <span
      title={missedExitLabel(p.missedExit)}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 animate-blink-red whitespace-nowrap"
    >
      <AlertTriangle className="w-3 h-3 shrink-0" />
      Missed
    </span>
  ) : null;

/** Stacked time (line 1, primary) + full date DD-MM-YYYY (line 2, muted). */
const TimeCell: React.FC<{ iso: string | null | undefined }> = ({ iso }) => {
  if (!iso) return <span className="text-gray-400">—</span>;
  if (Number.isNaN(new Date(iso).getTime())) return <span className="text-gray-400">—</span>;
  const { yyyy, mm, dd, hh, min, ss } = istParts(iso);
  const time = `${hh}:${min}:${ss}`;
  return (
    <div className="tabular-nums">
      <div className="text-xs text-gray-900 dark:text-white">{time}</div>
      <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{`${dd}-${mm}-${yyyy}`}</div>
    </div>
  );
};

// ── Indeterminate checkbox helper ──

const IndeterminateCheckbox: React.FC<{
  state: HeaderCheckboxState;
  onChange: () => void;
  className?: string;
}> = ({ state, onChange, className }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'all'}
      onChange={onChange}
      className={cn(
        'w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer',
        className,
      )}
    />
  );
};

// ── Collapse-state persistence (survives page navigation) ──
const STORAGE_EXPANDED_BASKETS = 'ptv-expanded-baskets';
const STORAGE_EXPANDED_STRATEGIES = 'ptv-expanded-strategies';
function readExpanded(key: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); }
  catch { return new Set(); }
}

// ── Main component ──

const PositionTableView: React.FC<PositionTableViewProps> = ({
  positions,
  onExit,
  onExitMultiple,
  onRetry,
  onTerminate,
  exitingId,
  retryingId,
  terminatingId,
  isToday = true,
}) => {
  const showActions = !!(onExit || onRetry || onTerminate);
  // Any non-terminal leg can be terminated (platform-only, never the broker).
  const isTerminable = (p: PositionRow) =>
    p.status === 'OPEN' || p.status === 'ENTRY_PENDING' || p.status === 'EXIT_PENDING';
  const showCheckboxes = !!onExitMultiple;


  // Strategy-details popup — opened by clicking the Basket · Strategy line in
  // flat mode. Shows only that strategy's legs without leaving the page.
  const [modalStrategy, setModalStrategy] = useState<{
    basketId: string;
    strategyId: string;
    basketName: string;
    strategyName: string;
    isMarketplace: boolean;
  } | null>(null);
  const modalPositions = useMemo(() => {
    if (!modalStrategy) return [] as PositionRow[];
    return positions.filter(
      (p) => p.basketId === modalStrategy.basketId && p.strategyId === modalStrategy.strategyId,
    );
  }, [positions, modalStrategy]);

  // ── Group-by-strategy state ──
  const [groupByStrategy, setGroupByStrategy] = useState(true);

  // ── Sort state (entry time / exit time / status) ──
  type SortColumn = 'entryTime' | 'exitTime' | 'status';
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Flat mode without an explicit sort choice defaults to newest entries
  // first. Grouped mode leaves rows in the parent's order (status-grouped).
  const effectiveSortColumn: SortColumn | null =
    sortColumn ?? (!groupByStrategy ? 'entryTime' : null);
  const effectiveSortDirection: 'asc' | 'desc' = sortColumn ? sortDirection : 'desc';

  const handleSort = useCallback((col: SortColumn) => {
    // Cycle: unsorted → asc → desc → unsorted
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }
    setSortColumn(null);
    setSortDirection('asc');
  }, [sortColumn, sortDirection]);

  const sortKey = useCallback((p: PositionRow): string | null => {
    if (effectiveSortColumn === 'entryTime') return p.entryFilledAt ?? p.entryTime ?? null;
    if (effectiveSortColumn === 'exitTime') return p.exitPlacedAt ?? p.exitTime ?? null;
    if (effectiveSortColumn === 'status') return getDisplayStatus(p);
    return null;
  }, [effectiveSortColumn]);

  const sortPositions = useCallback((rows: PositionRow[]): PositionRow[] => {
    if (!effectiveSortColumn) return rows;
    const dir = effectiveSortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      // Missing values always sink to the bottom, regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [effectiveSortColumn, effectiveSortDirection, sortKey]);
  // Track which baskets/strategies are EXPANDED (not collapsed). Empty by default
  // so all sections start collapsed regardless of when positions load. Persisted
  // in localStorage so the view is restored across page navigation.
  const [expandedBaskets, setExpandedBaskets] = useState<Set<string>>(
    () => readExpanded(STORAGE_EXPANDED_BASKETS),
  );
  const [expandedStrategies, setExpandedStrategies] = useState<Set<string>>(
    () => readExpanded(STORAGE_EXPANDED_STRATEGIES),
  );

  const groups = useMemo(
    () => (groupByStrategy ? buildNestedGroups(positions) : []),
    [positions, groupByStrategy],
  );

  // Expand / collapse all helpers.
  const allStrategyIds = useMemo(() => {
    if (!groupByStrategy) return [] as string[];
    return groups.flatMap((b) => b.strategies.map((s) => s.strategyId));
  }, [groups, groupByStrategy]);

  const allBasketIds = useMemo(() => groups.map((b) => b.basketId), [groups]);

  const allCollapsed = allBasketIds.length > 0 && allBasketIds.every((id) => !expandedBaskets.has(id));
  const noneCollapsed =
    allBasketIds.every((id) => expandedBaskets.has(id)) &&
    allStrategyIds.every((id) => expandedStrategies.has(id));

  const expandAll = () => {
    setExpandedBaskets(new Set(allBasketIds));
    setExpandedStrategies(new Set(allStrategyIds));
  };
  const collapseAll = () => {
    setExpandedBaskets(new Set());
  };

  // All selectable positions (includes collapsed — selection must survive collapse/expand).
  const selectablePositions = useMemo(() => {
    if (!groupByStrategy) return positions;
    const result: PositionRow[] = [];
    for (const basket of groups) {
      for (const strategy of basket.strategies) {
        result.push(...strategy.positions);
      }
    }
    return result;
  }, [positions, groupByStrategy, groups]);

  const selectableIds = useMemo(() => selectablePositions.map((p) => p.positionId), [selectablePositions]);

  // Visible positions (unexpanded baskets/strategies hidden — for rendering only).
  const displayedPositions = useMemo(() => {
    if (!groupByStrategy) return positions;
    const result: PositionRow[] = [];
    for (const basket of groups) {
      if (!expandedBaskets.has(basket.basketId)) continue;
      for (const strategy of basket.strategies) {
        if (expandedStrategies.has(strategy.strategyId)) {
          result.push(...strategy.positions);
        }
      }
    }
    return result;
  }, [positions, groupByStrategy, groups, expandedStrategies, expandedBaskets]);

  // ── Selection (uses selectableIds so collapsed positions stay selectable) ──
  const { selectedIds, isSelected, toggle, selectAll, clearAll, headerCheckboxState } =
    useRowSelection({ itemIds: selectableIds });

  // ── Persist expanded state across page navigations ──
  useEffect(() => {
    if (!groupByStrategy) return;
    try { localStorage.setItem(STORAGE_EXPANDED_BASKETS, JSON.stringify(Array.from(expandedBaskets))); } catch {}
  }, [expandedBaskets, groupByStrategy]);

  useEffect(() => {
    if (!groupByStrategy) return;
    try { localStorage.setItem(STORAGE_EXPANDED_STRATEGIES, JSON.stringify(Array.from(expandedStrategies))); } catch {}
  }, [expandedStrategies, groupByStrategy]);

  // ── Keyboard shortcuts ──
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!tableRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        selectAll();
      }
      if (e.key === 'Escape') {
        clearAll();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectAll, clearAll]);

  // ── Toggle strategy collapse ──
  const toggleStrategyCollapse = useCallback((strategyId: string) => {
    setExpandedStrategies((prev) => {
      const next = new Set(prev);
      if (next.has(strategyId)) next.delete(strategyId); // was expanded → collapse
      else next.add(strategyId);                         // was collapsed → expand
      return next;
    });
  }, []);

  // ── Toggle strategy checkbox ──
  const toggleStrategySelect = useCallback(
    (strategy: StrategyGroup) => {
      const ids = strategy.positions.map((p) => p.positionId);
      const allSelected = ids.every((id) => selectedIds.has(id));
      if (allSelected) {
        ids.forEach((id) => { if (selectedIds.has(id)) toggle(id); });
      } else {
        ids.forEach((id) => { if (!selectedIds.has(id)) toggle(id); });
      }
    },
    [selectedIds, toggle],
  );

  // ── Toggle basket collapse ──
  const toggleBasketCollapse = useCallback((basketId: string) => {
    setExpandedBaskets((prev) => {
      const next = new Set(prev);
      if (next.has(basketId)) next.delete(basketId); // was expanded → collapse
      else next.add(basketId);                       // was collapsed → expand
      return next;
    });
  }, []);

  // ── Toggle basket checkbox (selects/deselects all positions across all strategies) ──
  const toggleBasketSelect = useCallback(
    (basket: BasketGroup) => {
      const ids = basket.strategies.flatMap((s) => s.positions.map((p) => p.positionId));
      const allSelected = ids.every((id) => selectedIds.has(id));
      if (allSelected) {
        ids.forEach((id) => { if (selectedIds.has(id)) toggle(id); });
      } else {
        ids.forEach((id) => { if (!selectedIds.has(id)) toggle(id); });
      }
    },
    [selectedIds, toggle],
  );

  // ── Basket checkbox state (none / some / all) ──
  const basketCheckboxState = useCallback(
    (basket: BasketGroup): HeaderCheckboxState => {
      const ids = basket.strategies.flatMap((s) => s.positions.map((p) => p.positionId));
      if (ids.length === 0) return 'none';
      const count = ids.filter((id) => selectedIds.has(id)).length;
      if (count === 0) return 'none';
      if (count === ids.length) return 'all';
      return 'some';
    },
    [selectedIds],
  );

  // ── Batch action counts ──
  const selectedPositions = useMemo(
    () => positions.filter((p) => selectedIds.has(p.positionId)),
    [positions, selectedIds],
  );
  const openSelectedCount = selectedPositions.filter((p) => p.status === 'OPEN').length;
  const cancelableSelectedCount = selectedPositions.filter(isCancellable).length;

  // ── Empty state ──
  if (positions.length === 0) {
    return (
      <EmptyStateWithRedirect
        icon={<TrendingUp className="w-10 h-10" />}
        {...pickPositionsTableEmpty({ isToday })}
      />
    );
  }

  const colCount = 15 + (showCheckboxes ? 1 : 0) + (showActions ? 1 : 0);

  // ── Render a single position row ──
  const renderRow = (p: PositionRow, idx: number) => {
    const selected = isSelected(p.positionId);
    const displayStatus = getDisplayStatus(p);
    const isRejected = p.entryStatus === 'REJECTED';
    const showDetail = hasInlineDetail(p);
    // Don't draw border-b on the main row when a detail row follows — the detail draws it.
    const borderClass = showDetail ? '' : 'border-b border-gray-200 dark:border-gray-700';

    return (
      <React.Fragment key={`${p.positionId}-${idx}`}>
        <tr
          className={cn(
            'group transition-colors align-top',
            borderClass,
            rowBg(p, selected),
            rowOpacity(p),
            selected && 'border-l-2 border-l-blue-500',
            !selected && 'hover:bg-gray-50 dark:hover:bg-gray-700/50',
          )}
        >
          {/* Checkbox */}
          {showCheckboxes && (
            <td className="w-10 px-3 py-2.5 sticky left-0 z-10 bg-inherit">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => toggle(p.positionId, e.nativeEvent instanceof MouseEvent && (e.nativeEvent as MouseEvent).shiftKey)}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </td>
          )}

          {/* Symbol (multi-line: symbol + meta + optional WAT/ORB inline) */}
          <td
            className={cn(
              'px-3 py-2.5 sticky z-10 bg-inherit',
              showCheckboxes ? 'left-10' : 'left-0',
            )}
          >
            <div className="min-w-0">
              {!groupByStrategy && (
                <button
                  type="button"
                  onClick={() => setModalStrategy({
                    basketId: p.basketId,
                    strategyId: p.strategyId,
                    basketName: p.basketName || '',
                    strategyName: p.strategyName || '',
                    isMarketplace: p.isMarketplace === true,
                  })}
                  title={
                    p.isMarketplace
                      ? `Marketplace subscription — ${p.basketName || 'basket'} · ${p.strategyName || 'strategy'} positions`
                      : `View ${p.basketName || 'basket'} · ${p.strategyName || 'strategy'} positions`
                  }
                  className={cn(
                    'block w-full text-right text-[10px] transition-colors truncate leading-tight cursor-pointer',
                    p.isMarketplace
                      ? 'text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300'
                      : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400',
                  )}
                >
                  <span className="uppercase tracking-wider font-semibold">{p.basketName || '—'}</span>
                  {p.isMarketplace && (
                    <span
                      className={cn(
                        'inline-flex items-center align-middle ml-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider',
                        CHIP_DENSE.MARKETPLACE,
                      )}
                      title="Marketplace subscription"
                      aria-label="Marketplace subscription"
                    >
                      MP
                    </span>
                  )}
                  <span className="mx-1 text-gray-400 dark:text-gray-500">·</span>
                  <span>{p.strategyName || '—'}</span>
                </button>
              )}
              <div className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap text-right">
                {p.symbol}
              </div>
              <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 flex items-center justify-end gap-1.5">
                <span>{p.clientId} · L{p.legIndex ?? '—'} · {p.product}</span>
                <ReSequenceBadge p={p} />
                <EntrySourceBadge
                  source={p.entrySource}
                  reEntryCount={p.reEntryCount}
                  tpReexecCount={p.tpReexecCount}
                  reEntryConfig={p.reEntryConfig}
                  reExecuteConfig={p.reExecuteConfig}
                  variant="text"
                  size="xs"
                />
              </div>
              {(p.waitAndTradeConfig || p.orbConfig || p.isHedge) && (
                <div className="text-[10px] mt-0.5 flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 font-sans">
                  {p.isHedge && (
                    <span className="text-teal-700 dark:text-teal-300 uppercase tracking-wider font-bold" title="Protective hedge leg (BUY)">
                      Hedge
                    </span>
                  )}
                  {p.waitAndTradeConfig && (
                    <span className="text-purple-700 dark:text-purple-300">
                      <span className="uppercase tracking-wider font-bold">WAT</span>{' '}
                      {shortType(p.waitAndTradeConfig.type)} {p.waitAndTradeConfig.value}{(p.waitAndTradeConfig.type === 'PERCENTAGE' || p.waitAndTradeConfig.type === 'UL_PCT_UP' || p.waitAndTradeConfig.type === 'UL_PCT_DOWN') ? '%' : ''}
                      {p.waitAndTradeConfig.trigger > 0 && (
                        <> · trig <span className="tabular-nums">{p.waitAndTradeConfig.trigger.toFixed(2)}</span></>
                      )}
                    </span>
                  )}
                  {p.orbConfig && (
                    <span className="text-amber-700 dark:text-amber-300">
                      <span className="uppercase tracking-wider font-bold">ORB</span>{' '}
                      <span className="tabular-nums">{p.orbConfig.rangeLow.toFixed(2)}–{p.orbConfig.rangeHigh.toFixed(2)}</span>
                      {p.orbConfig.rangeStart && <> · {p.orbConfig.rangeStart}–{p.orbConfig.rangeEnd}</>}
                      {p.orbConfig.breakoutSide && <> · break {p.orbConfig.breakoutSide}</>}
                    </span>
                  )}
                </div>
              )}
            </div>
          </td>

          {/* Side */}
          <td className="px-3 py-2.5 text-center whitespace-nowrap">
            <SideBadge side={p.side} />
          </td>

          {/* Qty */}
          <td
            className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums text-sm text-gray-700 dark:text-gray-200"
            title={p.lots && p.lots > 0 ? `${p.qty} qty / ${p.lots} lots` : undefined}
          >
            {formatQtyWithLots(p.qty, p.lots)}
          </td>

          {/* ── ENTRY group (Time | Avg) ── */}
          {/* Time */}
          <td className="px-3 py-2.5 bg-green-50/30 dark:bg-green-900/5 border-l border-green-200/40 dark:border-green-800/40">
            <TimeCell iso={p.entryFilledAt ?? p.entryTime} />
          </td>
          {/* Avg */}
          <td className="px-3 py-2.5 text-right tabular-nums text-sm bg-green-50/30 dark:bg-green-900/5 border-r border-green-200/40 dark:border-green-800/40">
            <div className="text-gray-900 dark:text-white font-semibold">{formatPrice(p.averageEntryPrice ?? p.entryPrice)}</div>
          </td>

          {/* LTP — standalone, between Entry and SL so Trig sits beside it */}
          <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums text-sm font-semibold text-gray-900 dark:text-white">
            {formatPrice(p.ltp)}
          </td>

          {/* ── STOP-LOSS group ── */}
          {/* Trigger (+ limit line 2) — leftmost of group, content flush-left so the number sits next to LTP */}
          <td className="px-3 py-2.5 text-left tabular-nums text-xs bg-amber-50/30 dark:bg-amber-900/5 border-l border-amber-200/40 dark:border-amber-800/40">
            {p.slTriggerPrice != null ? (
              <div className="flex items-center gap-1.5">
                <span className="text-amber-700 dark:text-amber-300 font-semibold">{formatPrice(p.slTriggerPrice)}</span>
                {/* Phase 2 — circuit-cap recovery chip. Hidden by default
                    (slCircuitCapped defaults False for legacy / non-capped rows). */}
                {p.slCircuitCapped && (
                  <CappedChip
                    placed={p.slTriggerPrice}
                    desired={p.slDesiredTriggerPrice}
                    kind="Stop-Loss"
                    testId="position-table-capped-sl"
                  />
                )}
              </div>
            ) : (
              <div className="text-gray-400">—</div>
            )}
            {p.slLimitPrice != null && (
              <div className="text-[10px] text-amber-600/70 dark:text-amber-400/70 mt-0.5">
                Lmt {formatPrice(p.slLimitPrice)}
              </div>
            )}
          </td>
          {/* Cfg (type + value only) */}
          <td className="px-3 py-2.5 whitespace-nowrap bg-amber-50/30 dark:bg-amber-900/5 text-[11px] text-amber-700 dark:text-amber-300">
            {slConfigDisplay(p)}
          </td>
          {/* Time */}
          <td className="px-3 py-2.5 bg-amber-50/30 dark:bg-amber-900/5">
            <TimeCell iso={p.slUpdatedAt ?? p.slPlacedAt} />
          </td>
          {/* Status (SL) — rightmost of SL group */}
          <td className="px-3 py-2.5 text-right whitespace-nowrap bg-amber-50/30 dark:bg-amber-900/5 border-r border-amber-200/40 dark:border-amber-800/40">
            {p.slStatus ? (
              <Badge variant={statusBadgeVariant[p.slStatus] || 'default'} size="sm">{p.slStatus}</Badge>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>

          {/* ── EXIT group (Avg left · Time middle · Type right) ── */}
          {/* Avg — flush left so the number sits at the group's left edge */}
          <td className="px-3 py-2.5 text-left whitespace-nowrap tabular-nums text-sm bg-red-50/30 dark:bg-red-900/5 border-l border-red-200/40 dark:border-red-800/40">
            {p.exitPrice != null || p.exitAveragePrice != null ? (
              <span className="text-gray-900 dark:text-white font-semibold">{formatPrice(p.exitAveragePrice ?? p.exitPrice)}</span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
          {/* Time — stacked time + date, natural middle */}
          <td className="px-3 py-2.5 text-center bg-red-50/30 dark:bg-red-900/5">
            <TimeCell iso={p.exitPlacedAt ?? p.exitTime} />
          </td>
          {/* Type — right-aligned */}
          <td className="px-3 py-2.5 text-right text-xs text-gray-600 dark:text-gray-300 uppercase tracking-wider font-semibold bg-red-50/30 dark:bg-red-900/5 border-r border-red-200/40 dark:border-red-800/40">
            {p.exitType || <span className="normal-case tracking-normal font-normal text-gray-400">—</span>}
          </td>

          {/* P&L — END column */}
          <td className="px-3 py-2.5 text-right whitespace-nowrap bg-emerald-50/30 dark:bg-emerald-900/5 border-l border-r border-emerald-200/40 dark:border-emerald-800/40">
            <PnlFlash value={p.pnl} className="text-sm font-semibold" />
          </td>

          {/* Status — END column. Stacks the displayStatus Badge above a
              compact MISSED pill so the user sees the missed-exit signal in
              the same column they're already scanning for status — no need
              to look at the wide Symbol/sub-row area. */}
          <td className="px-3 py-2.5 text-center whitespace-nowrap">
            <div className="inline-flex flex-col items-center gap-1">
              <Badge variant={statusBadgeVariant[displayStatus] || 'warning'} size="sm">
                {displayStatus}
              </Badge>
              <MissedExitPill p={p} />
            </div>
          </td>

          {/* Actions */}
          {showActions && (
            <td className="px-3 py-2.5 whitespace-nowrap sticky right-0 z-10 bg-inherit">
              <div className="flex items-center gap-1">
                {isRejected && onRetry && (
                  <button type="button" onClick={() => onRetry(p)} disabled={retryingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <RotateCcw className="w-3 h-3" /> Retry
                  </button>
                )}
                {isCancellable(p) && onExit && (
                  <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <Ban className="w-3 h-3" /> Cancel
                  </button>
                )}
                {p.status === 'OPEN' && onExit && (
                  <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <LogOut className="w-3 h-3" /> Exit
                  </button>
                )}
                {p.status === 'EXIT_PENDING' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded cursor-not-allowed">
                    <LogOut className="w-3 h-3" /> Exiting…
                  </span>
                )}
                {isTerminable(p) && onTerminate && (
                  <button type="button" onClick={() => onTerminate(p)} disabled={terminatingId === p.positionId}
                    title="Stop the platform from managing this position (does not touch the broker)"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <Unplug className="w-3 h-3" /> Terminate
                  </button>
                )}
              </div>
            </td>
          )}
        </tr>

        {/* ── Always-visible inline detail sub-row (WAT / ORB / error) ── */}
        {showDetail && (
          <tr
            className={cn(
              'border-b border-gray-200 dark:border-gray-700',
              rowBg(p, selected),
              rowOpacity(p),
              selected && 'border-l-2 border-l-blue-500',
            )}
          >
            {showCheckboxes && <td className="sticky left-0 z-10 bg-inherit" />}
            <td
              colSpan={colCount - (showCheckboxes ? 1 : 0) - (showActions ? 1 : 0)}
              className="px-3 pb-2.5 pt-0"
            >
              <div className="flex flex-wrap gap-2">
                {isRejected && p.entryError && (
                  <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900 px-2.5 py-1 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
                    <span className="text-[10px] text-red-700 dark:text-red-300 font-semibold break-words min-w-0">
                      {p.entryError}
                    </span>
                  </div>
                )}
                {/* Missed-exit moved to the Status column (MissedExitPill).
                    Kept out of this sub-row so the long Symbol-area band is
                    only used for broker-side rejection details. */}
              </div>
            </td>
            {showActions && <td className="sticky right-0 z-10 bg-inherit" />}
          </tr>
        )}
      </React.Fragment>
    );
  };

  // ── Strategy sub-header row ──
  const renderStrategyHeader = (basket: BasketGroup, strategy: StrategyGroup) => {
    const collapsed = !expandedStrategies.has(strategy.strategyId);
    const strategyIds = strategy.positions.map((p) => p.positionId);
    const allSelected = strategyIds.length > 0 && strategyIds.every((id) => selectedIds.has(id));

    // Matches the strategy-header pattern used elsewhere in the project
    // (CompactCardView) for visual consistency across tables.
    const headerBg = 'bg-gray-50 dark:bg-gray-800/80';
    const headerBorder = 'border-b border-gray-200 dark:border-gray-700';

    return (
      <tr
        key={`strat-${strategy.strategyId}`}
        className={cn(headerBg, headerBorder)}
      >
        {/* Checkbox */}
        {showCheckboxes && (
          <td className={cn('px-3 py-2 sticky left-0 z-10', headerBg)}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => toggleStrategySelect(strategy)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
          </td>
        )}

        {/* Symbol — basket · strategy name + chevron + leg count */}
        <td className={cn('px-3 py-2 sticky z-10', headerBg, showCheckboxes ? 'left-10' : 'left-0')}>
          <button
            type="button"
            onClick={() => toggleStrategyCollapse(strategy.strategyId)}
            className="flex items-start gap-2 text-left min-w-0 group w-full"
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 text-gray-500 transition-transform shrink-0 mt-0.5',
                collapsed && '-rotate-90',
              )}
            />
            <div className="min-w-0 flex-1 pl-4">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-sm font-semibold truncate transition-colors leading-tight',
                    basket.isMarketplace
                      ? 'text-violet-700 dark:text-violet-300 group-hover:text-violet-800 dark:group-hover:text-violet-200'
                      : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400',
                  )}
                >
                  {strategy.strategyName || '—'}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap shrink-0">
                  ({strategy.positions.length} {strategy.positions.length === 1 ? 'leg' : 'legs'})
                </span>
              </div>
            </div>
          </button>
        </td>

        {/* Side — empty */}
        <td className="px-3 py-2" />
        {/* Qty — empty */}
        <td className="px-3 py-2" />

        {/* Entry: Time, Avg — empty (no tint so strategy bg dominates; keep group borders) */}
        <td className="px-3 py-2 border-l border-green-200/40 dark:border-green-800/40" />
        <td className="px-3 py-2 border-r border-green-200/40 dark:border-green-800/40" />

        {/* LTP — empty */}
        <td className="px-3 py-2" />

        {/* SL: Trig, Time, Cfg, Status — empty (no tint; keep group borders) */}
        <td className="px-3 py-2 border-l border-amber-200/40 dark:border-amber-800/40" />
        <td className="px-3 py-2" />
        <td className="px-3 py-2" />
        <td className="px-3 py-2 border-r border-amber-200/40 dark:border-amber-800/40" />

        {/* Exit: Avg, Time, Type — empty (no tint; keep group borders) */}
        <td className="px-3 py-2 border-l border-red-200/40 dark:border-red-800/40" />
        <td className="px-3 py-2" />
        <td className="px-3 py-2 border-r border-red-200/40 dark:border-red-800/40" />

        {/* P&L — strategy total */}
        <td className="px-3 py-2 text-right whitespace-nowrap border-l border-r border-emerald-200/40 dark:border-emerald-800/40">
          <PnlFlash value={strategy.pnl} className="text-sm font-semibold" />
        </td>

        {/* Status — 7-box count */}
        <td className="px-3 py-2 text-center whitespace-nowrap">
          <StatusBoxRow
            counts={{
              open: strategy.openCount,
              pending: strategy.triggerPendingCount,
              openOrders: strategy.openOrdersCount,
              error: strategy.rejectedAxisCount,
              // Missed-exit count comes from per-row `missedExit` field stamped
              // by `scheduleToPositionRow` when the backend flags today's view.
              // The Missed box blinks red when count > 0.
              missed: strategy.missedCount,
              cancelled: strategy.cancelledAxisCount,
              closed: strategy.closedCount,
            }}
          />
        </td>

        {/* Actions — Exit All / Cancel All scoped to this strategy */}
        {showActions && (
          <td className={cn('px-3 py-2 sticky right-0 z-10', headerBg)}>
            {(() => {
              const exitableIds = strategy.positions
                .filter((p) => p.status === 'OPEN')
                .map((p) => p.positionId);
              const cancellableIds = strategy.positions
                .filter(isCancellable)
                .map((p) => p.positionId);
              return (
                <div className="flex items-center gap-1">
                  {exitableIds.length > 0 && onExitMultiple && (
                    <button
                      type="button"
                      onClick={() => onExitMultiple(exitableIds)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors whitespace-nowrap"
                    >
                      <LogOut className="w-3 h-3" /> Exit ({exitableIds.length})
                    </button>
                  )}
                  {cancellableIds.length > 0 && onExitMultiple && (
                    <button
                      type="button"
                      onClick={() => onExitMultiple(cancellableIds)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors whitespace-nowrap"
                    >
                      <Ban className="w-3 h-3" /> Cancel ({cancellableIds.length})
                    </button>
                  )}
                </div>
              );
            })()}
          </td>
        )}
      </tr>
    );
  };

  // ── Basket header row ──
  const renderBasketHeader = (basket: BasketGroup) => {
    const collapsed = !expandedBaskets.has(basket.basketId);
    const basketBg = 'bg-gray-100 dark:bg-gray-700';
    const allPositions = basket.strategies.flatMap((s) => s.positions);
    const exitableIds = allPositions.filter((p) => p.status === 'OPEN').map((p) => p.positionId);
    const cancellableIds = allPositions.filter(isCancellable).map((p) => p.positionId);

    return (
      <tr
        key={`basket-${basket.basketId}`}
        className={cn(basketBg, 'border-b-2 border-gray-300 dark:border-gray-600')}
      >
        {/* Checkbox */}
        {showCheckboxes && (
          <td className={cn('px-3 py-2.5 sticky left-0 z-10', basketBg)}>
            <IndeterminateCheckbox
              state={basketCheckboxState(basket)}
              onChange={() => toggleBasketSelect(basket)}
            />
          </td>
        )}

        {/* Basket name + collapse toggle */}
        <td className={cn('px-3 py-2.5 sticky z-10', basketBg, showCheckboxes ? 'left-10' : 'left-0')}>
          <button
            type="button"
            onClick={() => toggleBasketCollapse(basket.basketId)}
            className="flex items-center gap-2 text-left group w-full"
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 text-gray-500 transition-transform shrink-0',
                collapsed && '-rotate-90',
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-sm font-bold truncate transition-colors uppercase tracking-wide',
                    basket.isMarketplace
                      ? 'text-violet-700 dark:text-violet-300 group-hover:text-violet-800 dark:group-hover:text-violet-200'
                      : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400',
                  )}
                >
                  {basket.basketName || '—'}
                </span>
                {basket.isMarketplace && (
                  <span
                    className={cn(
                      'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shrink-0',
                      CHIP_DENSE.MARKETPLACE,
                    )}
                    title="Marketplace subscription"
                    aria-label="Marketplace subscription"
                  >
                    MP
                  </span>
                )}
                <span className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap shrink-0">
                  {basket.strategies.length} {basket.strategies.length === 1 ? 'strategy' : 'strategies'} · {basket.totalPositions} {basket.totalPositions === 1 ? 'leg' : 'legs'}
                </span>
              </div>
            </div>
          </button>
        </td>

        {/* Side — empty */}
        <td className="px-3 py-2.5" />
        {/* Qty — empty */}
        <td className="px-3 py-2.5" />

        {/* Entry columns — empty */}
        <td className="px-3 py-2.5 border-l border-green-200/40 dark:border-green-800/40" />
        <td className="px-3 py-2.5 border-r border-green-200/40 dark:border-green-800/40" />

        {/* LTP — empty */}
        <td className="px-3 py-2.5" />

        {/* SL columns — empty */}
        <td className="px-3 py-2.5 border-l border-amber-200/40 dark:border-amber-800/40" />
        <td className="px-3 py-2.5" />
        <td className="px-3 py-2.5" />
        <td className="px-3 py-2.5 border-r border-amber-200/40 dark:border-amber-800/40" />

        {/* Exit columns — empty */}
        <td className="px-3 py-2.5 border-l border-red-200/40 dark:border-red-800/40" />
        <td className="px-3 py-2.5" />
        <td className="px-3 py-2.5 border-r border-red-200/40 dark:border-red-800/40" />

        {/* P&L — basket total */}
        <td className="px-3 py-2.5 text-right whitespace-nowrap border-l border-r border-emerald-200/40 dark:border-emerald-800/40">
          <PnlFlash value={basket.pnl} className="text-sm font-bold" />
        </td>

        {/* Status — basket-level 7-box count */}
        <td className="px-3 py-2.5 text-center whitespace-nowrap">
          <StatusBoxRow
            counts={{
              open: basket.openCount,
              pending: basket.triggerPendingCount,
              openOrders: basket.openOrdersCount,
              error: basket.rejectedAxisCount,
              missed: basket.missedCount,
              cancelled: basket.cancelledAxisCount,
              closed: basket.closedCount,
            }}
          />
        </td>

        {/* Actions — Exit All / Cancel All scoped to this basket */}
        {showActions && (
          <td className={cn('px-3 py-2.5 sticky right-0 z-10', basketBg)}>
            <div className="flex items-center gap-1">
              {exitableIds.length > 0 && onExitMultiple && (
                <button
                  type="button"
                  onClick={() => onExitMultiple(exitableIds)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors whitespace-nowrap"
                >
                  <LogOut className="w-3 h-3" /> Exit ({exitableIds.length})
                </button>
              )}
              {cancellableIds.length > 0 && onExitMultiple && (
                <button
                  type="button"
                  onClick={() => onExitMultiple(cancellableIds)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors whitespace-nowrap"
                >
                  <Ban className="w-3 h-3" /> Cancel ({cancellableIds.length})
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  // ── Build the table body (flat or grouped) ──
  const tableBody = groupByStrategy
    ? groups.flatMap((basket) => [
        renderBasketHeader(basket),
        ...(expandedBaskets.has(basket.basketId)
          ? basket.strategies.flatMap((strategy) => [
              renderStrategyHeader(basket, strategy),
              ...(expandedStrategies.has(strategy.strategyId)
                ? sortPositions(strategy.positions).map((p, idx) => renderRow(p, idx))
                : []),
            ])
          : []),
      ])
    : sortPositions(displayedPositions).map((p, idx) => renderRow(p, idx));

  // ── Mobile card ──
  const mobileCard = (p: PositionRow, idx: number) => {
    const selected = isSelected(p.positionId);
    const displayStatus = getDisplayStatus(p);
    const isRejected = p.entryStatus === 'REJECTED';

    return (
      <div
        key={`${p.positionId}-${idx}`}
        className={cn(
          'rounded-lg border p-3 transition-colors',
          selected
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        )}
      >
        <div className="flex items-start gap-2">
          {showCheckboxes && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) =>
                toggle(p.positionId, e.nativeEvent instanceof MouseEvent && (e.nativeEvent as MouseEvent).shiftKey)
              }
              className="w-4 h-4 mt-0.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{p.symbol}</span>
              {/* Status cluster: displayStatus Badge + (when present) the
                  MISSED pill — placed adjacent (not stacked) so the row
                  header line stays compact on mobile. */}
              <div className="inline-flex items-center gap-1 shrink-0">
                <Badge variant={statusBadgeVariant[displayStatus] || 'warning'} size="sm">{displayStatus}</Badge>
                <MissedExitPill p={p} />
              </div>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <SideBadge side={p.side} />
                <span
                  className="tabular-nums text-gray-700 dark:text-gray-200 font-semibold"
                  title={p.lots && p.lots > 0 ? `${p.qty} qty / ${p.lots} lots` : undefined}
                >
                  ×{formatQtyWithLots(p.qty, p.lots)}
                </span>
                <span className="text-gray-400">{p.clientId}</span>
              </div>
              <PnlFlash value={p.pnl} className="text-sm font-bold" />
            </div>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
              <span>Entry: <span className="text-gray-900 dark:text-white tabular-nums">{formatPrice(p.averageEntryPrice ?? p.entryPrice)}</span></span>
              <span>LTP: <span className="text-gray-900 dark:text-white tabular-nums font-semibold">{formatPrice(p.ltp)}</span></span>
              {p.slTriggerPrice && (
                <span className="inline-flex items-center gap-1">
                  SL: <span className="text-gray-900 dark:text-white tabular-nums">{formatPrice(p.slTriggerPrice)}</span>
                  {p.slCircuitCapped && (
                    <CappedChip
                      placed={p.slTriggerPrice}
                      desired={p.slDesiredTriggerPrice}
                      kind="Stop-Loss"
                      testId="position-table-mobile-capped-sl"
                    />
                  )}
                </span>
              )}
            </div>

            {/* Inline HEDGE/WAT/ORB/error callouts */}
            {p.isHedge && (
              <div className="mt-1.5 rounded bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-900 px-2 py-1 text-[10px] text-teal-800 dark:text-teal-200">
                <span className="font-bold">HEDGE</span> · protective BUY leg
              </div>
            )}
            {p.waitAndTradeConfig && (
              <div className="mt-1.5 rounded bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-900 px-2 py-1 text-[10px] text-purple-800 dark:text-purple-200">
                <span className="font-bold">WAT</span> {shortType(p.waitAndTradeConfig.type)} {p.waitAndTradeConfig.value}{(p.waitAndTradeConfig.type === 'PERCENTAGE' || p.waitAndTradeConfig.type === 'UL_PCT_UP' || p.waitAndTradeConfig.type === 'UL_PCT_DOWN') ? '%' : ''}
                {p.waitAndTradeConfig.candleClose != null && <> · close {p.waitAndTradeConfig.candleClose.toFixed(2)} + offset {(p.waitAndTradeConfig.offset ?? 0) >= 0 ? '+' : ''}{(p.waitAndTradeConfig.offset ?? 0).toFixed(2)}</>}
                {p.waitAndTradeConfig.trigger > 0 && <> → trigger {p.waitAndTradeConfig.trigger.toFixed(2)}</>}
              </div>
            )}
            {p.orbConfig && (
              <div className="mt-1.5 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900 px-2 py-1 text-[10px] text-amber-800 dark:text-amber-200">
                <span className="font-bold">ORB</span> range {p.orbConfig.rangeLow.toFixed(2)} – {p.orbConfig.rangeHigh.toFixed(2)}
                {p.orbConfig.rangeStart && <> · {p.orbConfig.rangeStart} – {p.orbConfig.rangeEnd}</>}
                {p.orbConfig.breakoutSide && <> · breakout {p.orbConfig.breakoutSide}</>}
              </div>
            )}
            {isRejected && p.entryError && (
              <div className="mt-1.5 flex gap-1.5 text-[10px] text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                <span className="break-words min-w-0">{p.entryError}</span>
              </div>
            )}
            {/* Missed-exit now renders as a compact MISSED pill alongside the
                status Badge in the row header (see above). The full reason is
                in the pill's tooltip. */}

            {/* Actions */}
            {showActions && (
              <div className="mt-2 flex items-center justify-end gap-1.5">
                {isRejected && onRetry && (
                  <button type="button" onClick={() => onRetry(p)} disabled={retryingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-white bg-orange-600 hover:bg-orange-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <RotateCcw className="w-3 h-3" /> Retry
                  </button>
                )}
                {isCancellable(p) && onExit && (
                  <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <Ban className="w-3 h-3" /> Cancel
                  </button>
                )}
                {p.status === 'OPEN' && onExit && (
                  <button type="button" onClick={() => onExit(p)} disabled={exitingId === p.positionId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <LogOut className="w-3 h-3" /> Exit
                  </button>
                )}
                {isTerminable(p) && onTerminate && (
                  <button type="button" onClick={() => onTerminate(p)} disabled={terminatingId === p.positionId}
                    title="Stop the platform from managing this position (does not touch the broker)"
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    <Unplug className="w-3 h-3" /> Terminate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Floating batch action bar ──
  const batchBar = selectedIds.size > 0 && showCheckboxes && (
    <div className="sticky bottom-4 z-20 flex justify-center px-4 mt-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg px-4 py-2.5 flex items-center gap-3 animate-in slide-in-from-bottom-2 duration-200">
        <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">
          <span className="text-blue-600 dark:text-blue-400 font-bold">{selectedIds.size}</span> selected
        </span>
        <button type="button" onClick={clearAll}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
          <X className="w-3 h-3" /> Clear
        </button>
        {cancelableSelectedCount > 0 && onExitMultiple && (
          <button type="button"
            onClick={() => onExitMultiple(selectedPositions.filter(isCancellable).map((p) => p.positionId))}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md transition-colors">
            <Ban className="w-3 h-3" /> Cancel ({cancelableSelectedCount})
          </button>
        )}
        {openSelectedCount > 0 && onExitMultiple && (
          <button type="button"
            onClick={() => onExitMultiple(selectedPositions.filter((p) => p.status === 'OPEN').map((p) => p.positionId))}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors">
            <LogOut className="w-3 h-3" /> Exit ({openSelectedCount})
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div ref={tableRef} tabIndex={-1} className="outline-none relative">
      {/* Toolbar: expand/collapse + group toggle */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 flex-wrap">
        {groupByStrategy && (
          <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <button type="button" onClick={expandAll} disabled={noneCollapsed} title="Expand all"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronsUpDown className="w-3.5 h-3.5" /> Expand
            </button>
            <button type="button" onClick={collapseAll} disabled={allCollapsed} title="Collapse all"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-l border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronsDownUp className="w-3.5 h-3.5" /> Collapse
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Group</span>
          <ToggleSwitch
            checked={groupByStrategy}
            onChange={() => setGroupByStrategy((v) => !v)}
            size="sm"
            label="Group"
          />
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-20">
            {/* Row 1: Column group labels */}
            <tr className="bg-gray-50 dark:bg-gray-700">
              {showCheckboxes && <th className="bg-gray-50 dark:bg-gray-700 sticky left-0 z-30" />}
              <th
                className={cn('bg-gray-50 dark:bg-gray-700 sticky z-30', showCheckboxes ? 'left-10' : 'left-0')}
                colSpan={3}
              />
              <th colSpan={2} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-green-600 dark:text-green-400 border-b-2 border-green-500/40 bg-gray-50 dark:bg-gray-700">
                Entry
              </th>
              <th className="bg-gray-50 dark:bg-gray-700" />
              <th colSpan={4} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400 border-b-2 border-amber-500/40 bg-gray-50 dark:bg-gray-700">
                Stop-Loss
              </th>
              <th colSpan={3} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-red-600 dark:text-red-400 border-b-2 border-red-500/40 bg-gray-50 dark:bg-gray-700">
                Exit
              </th>
              <th className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500/40 bg-gray-50 dark:bg-gray-700">
                P&amp;L
              </th>
              <th className="bg-gray-50 dark:bg-gray-700" />
              {showActions && <th className="bg-gray-50 dark:bg-gray-700 sticky right-0 z-30" />}
            </tr>
            {/* Row 2: Individual column labels */}
            <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              {showCheckboxes && (
                <th className="w-10 px-3 py-2 sticky left-0 z-30 bg-gray-50 dark:bg-gray-700">
                  <IndeterminateCheckbox
                    state={headerCheckboxState}
                    onChange={() => (headerCheckboxState === 'all' ? clearAll() : selectAll())}
                  />
                </th>
              )}
              <th className={cn(TH_R, 'sticky z-30 bg-gray-50 dark:bg-gray-700', showCheckboxes ? 'left-10' : 'left-0')}>Symbol</th>
              <th className={TH_C}>Side</th>
              <th className={TH_R}>Qty</th>
              <th className={cn(TH, 'bg-green-50/40 dark:bg-green-900/10 border-l border-green-200/40 dark:border-green-800/40')}>
                <button
                  type="button"
                  onClick={() => handleSort('entryTime')}
                  className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-gray-900 dark:hover:text-white',
                    effectiveSortColumn === 'entryTime' && 'text-gray-900 dark:text-white',
                  )}
                  aria-label="Sort by entry time"
                >
                  <span>Time</span>
                  {effectiveSortColumn === 'entryTime'
                    ? (effectiveSortDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                </button>
              </th>
              <th className={cn(TH_R, 'bg-green-50/40 dark:bg-green-900/10 border-r border-green-200/40 dark:border-green-800/40')}>Avg</th>
              <th className={TH_R}>LTP</th>
              <th className={cn(TH, 'bg-amber-50/40 dark:bg-amber-900/10 border-l border-amber-200/40 dark:border-amber-800/40')}>Trig</th>
              <th className={cn(TH, 'bg-amber-50/40 dark:bg-amber-900/10')}>Cfg</th>
              <th className={cn(TH, 'bg-amber-50/40 dark:bg-amber-900/10')}>Time</th>
              <th className={cn(TH_R, 'bg-amber-50/40 dark:bg-amber-900/10 border-r border-amber-200/40 dark:border-amber-800/40')}>Status</th>
              <th className={cn(TH, 'bg-red-50/40 dark:bg-red-900/10 border-l border-red-200/40 dark:border-red-800/40')}>Avg</th>
              <th className={cn(TH_C, 'bg-red-50/40 dark:bg-red-900/10')}>
                <button
                  type="button"
                  onClick={() => handleSort('exitTime')}
                  className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-gray-900 dark:hover:text-white',
                    effectiveSortColumn === 'exitTime' && 'text-gray-900 dark:text-white',
                  )}
                  aria-label="Sort by exit time"
                >
                  <span>Time</span>
                  {effectiveSortColumn === 'exitTime'
                    ? (effectiveSortDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                </button>
              </th>
              <th className={cn(TH_R, 'bg-red-50/40 dark:bg-red-900/10 border-r border-red-200/40 dark:border-red-800/40')}>Type</th>
              <th className={cn(TH_R, 'bg-emerald-50/40 dark:bg-emerald-900/10 border-l border-r border-emerald-200/40 dark:border-emerald-800/40')}>₹</th>
              <th className={TH_C}>
                <button
                  type="button"
                  onClick={() => handleSort('status')}
                  className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-gray-900 dark:hover:text-white',
                    effectiveSortColumn === 'status' && 'text-gray-900 dark:text-white',
                  )}
                  aria-label="Sort by status"
                >
                  <span>Status</span>
                  {effectiveSortColumn === 'status'
                    ? (effectiveSortDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3" />
                        : <ArrowDown className="w-3 h-3" />)
                    : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                </button>
              </th>
              {showActions && (
                <th className={cn(TH_C, 'sticky right-0 z-30 bg-gray-50 dark:bg-gray-700')}>Action</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">{tableBody}</tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3 p-2">
        {groupByStrategy
          ? groups.map((basket) => {
              const basketCollapsed = !expandedBaskets.has(basket.basketId);
              const basketExitableIds = basket.strategies
                .flatMap((s) => s.positions)
                .filter((p) => p.status === 'OPEN')
                .map((p) => p.positionId);
              const basketCancellableIds = basket.strategies
                .flatMap((s) => s.positions)
                .filter(isCancellable)
                .map((p) => p.positionId);
              return (
                <div key={basket.basketId} className="space-y-2">
                  {/* Basket section header */}
                  <div className="flex items-center justify-between gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleBasketCollapse(basket.basketId)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left group"
                    >
                      <ChevronDown
                        className={cn('w-4 h-4 text-gray-500 transition-transform shrink-0', basketCollapsed && '-rotate-90')}
                      />
                      <div className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'text-sm font-bold uppercase tracking-wide transition-colors',
                            basket.isMarketplace
                              ? 'text-violet-700 dark:text-violet-300 group-hover:text-violet-800 dark:group-hover:text-violet-200'
                              : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400',
                          )}
                        >
                          {basket.basketName || '—'}
                        </span>
                        {basket.isMarketplace && (
                          <span
                            className={cn(
                              'inline-flex items-center align-middle ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
                              CHIP_DENSE.MARKETPLACE,
                            )}
                            title="Marketplace subscription"
                            aria-label="Marketplace subscription"
                          >
                            MP
                          </span>
                        )}
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-2">
                          {basket.strategies.length}s · {basket.totalPositions}l
                        </span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <PnlFlash value={basket.pnl} className="text-xs font-bold" />
                      {basketExitableIds.length > 0 && onExitMultiple && (
                        <button type="button" onClick={() => onExitMultiple(basketExitableIds)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors">
                          <LogOut className="w-3 h-3" /> Exit ({basketExitableIds.length})
                        </button>
                      )}
                      {basketCancellableIds.length > 0 && onExitMultiple && (
                        <button type="button" onClick={() => onExitMultiple(basketCancellableIds)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors">
                          <Ban className="w-3 h-3" /> Cancel ({basketCancellableIds.length})
                        </button>
                      )}
                    </div>
                  </div>
                  {!basketCollapsed &&
                    basket.strategies.map((strategy) => {
                      const stratCollapsed = !expandedStrategies.has(strategy.strategyId);
                      const stratExitableIds = strategy.positions.filter((p) => p.status === 'OPEN').map((p) => p.positionId);
                      const stratCancellableIds = strategy.positions.filter(isCancellable).map((p) => p.positionId);
                      return (
                        <div key={strategy.strategyId} className="ml-2 space-y-1.5">
                          {/* Strategy section header */}
                          <div className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-gray-800/80 rounded px-3 py-1.5 border border-gray-200 dark:border-gray-700">
                            <button
                              type="button"
                              onClick={() => toggleStrategyCollapse(strategy.strategyId)}
                              className="flex items-center gap-1.5 flex-1 min-w-0 text-left group"
                            >
                              <ChevronDown
                                className={cn('w-3.5 h-3.5 text-gray-400 transition-transform shrink-0', stratCollapsed && '-rotate-90')}
                              />
                              <span
                                className={cn(
                                  'text-xs font-semibold truncate transition-colors',
                                  basket.isMarketplace
                                    ? 'text-violet-700 dark:text-violet-300 group-hover:text-violet-800 dark:group-hover:text-violet-200'
                                    : 'text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400',
                                )}
                              >
                                {strategy.strategyName || '—'}
                              </span>
                              <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">
                                ({strategy.positions.length})
                              </span>
                            </button>
                            <div className="flex items-center gap-1 shrink-0">
                              <PnlFlash value={strategy.pnl} className="text-xs font-semibold" />
                              {stratExitableIds.length > 0 && onExitMultiple && (
                                <button type="button" onClick={() => onExitMultiple(stratExitableIds)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors">
                                  <LogOut className="w-3 h-3" /> Exit
                                </button>
                              )}
                              {stratCancellableIds.length > 0 && onExitMultiple && (
                                <button type="button" onClick={() => onExitMultiple(stratCancellableIds)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-white bg-gray-600 hover:bg-gray-700 rounded transition-colors">
                                  <Ban className="w-3 h-3" /> Cancel
                                </button>
                              )}
                            </div>
                          </div>
                          {!stratCollapsed &&
                            sortPositions(strategy.positions).map((p, idx) => mobileCard(p, idx))}
                        </div>
                      );
                    })}
                </div>
              );
            })
          : sortPositions(displayedPositions).map((p, idx) => mobileCard(p, idx))}
      </div>

      {/* Floating batch action bar */}
      {batchBar}

      {/* Strategy-details popup */}
      {modalStrategy && (
        <StrategyPositionsModal
          basketName={modalStrategy.basketName}
          strategyName={modalStrategy.strategyName}
          isMarketplace={modalStrategy.isMarketplace}
          positions={modalPositions}
          onClose={() => setModalStrategy(null)}
          onExit={onExit}
          onRetry={onRetry}
          onTerminate={onTerminate}
          exitingId={exitingId}
          retryingId={retryingId}
          terminatingId={terminatingId}
        />
      )}
    </div>
  );
};


export default PositionTableView;
